import { useCallback } from 'react';
import { Route, Routes } from 'react-router-dom';

import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { DELTA_1_BPS_TOGGLE, FEES_TOGGLE, FIRM_LIQUIDITY_TOGGLE } from '@src/tokens/tooltips';
import { TourName } from '@src/tokens/tours';
import {
  ButtonVariants,
  FeeModeEnum,
  Flex,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  MixpanelSourceContext,
  Popover,
  Stack,
  Toggle,
  Tour,
  Z_INDEX,
  useIsMinWidth,
  useMixpanel,
  usePopoverState,
  useSecuritiesContext,
} from '@talos/kyoko';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import { HeaderButton } from './HeaderButton';
import { HeaderButtons } from './HeaderButtons';
import { WatchlistButton } from './WatchlistButton';
import { HeaderWrapper } from './styles';

const POPOVER_TARGET_STYLE = { height: '100%' };
// The tooltip should appear behind the toggle dropdown
const COMPACT_TOGGLE_TOOLTIP_PROPS = {
  zIndex: Z_INDEX.dropdown + 1,
} as const;
const COMPACT_TOGGLE_MIN_WIDTH = 1200;
const COMPACT_TOGGLE_LABEL_MIN_WIDTH = 1080;

export const Header = () => {
  return (
    <MixpanelSourceContext.Provider value={MixpanelEventSource.Header}>
      <InternalHeader />
    </MixpanelSourceContext.Provider>
  );
};

const InternalHeader = () => {
  const mixpanel = useMixpanel();
  const { hasBPSPricingSecurities } = useSecuritiesContext();
  const { enableDelta1BPSPricingMode } = useFeatureFlag();
  const {
    showAllInPrices,
    setShowAllInPrices,
    showFirmLiquidity,
    setShowFirmLiquidity,
    showDelta1BPS,
    setShowDelta1BPS,
  } = useDisplaySettings();

  const handleToggleAllInPrices = useCallback(
    (showAllInPrices: boolean) => {
      const fees = showAllInPrices ? FeeModeEnum.Taker : null;
      mixpanel.track(MixpanelEvent.ToggleFees, { [MixpanelEventProperty.Enabled]: fees });
      setShowAllInPrices(fees);
    },
    [mixpanel, setShowAllInPrices]
  );

  const handleToggleFirmLiquidity = useCallback(
    (showFirmLiquidity: boolean) => {
      mixpanel.track(MixpanelEvent.ToggleFirmLiquidity, { [MixpanelEventProperty.Enabled]: showFirmLiquidity });
      setShowFirmLiquidity(showFirmLiquidity);
    },
    [mixpanel, setShowFirmLiquidity]
  );

  const handleToggleDelta1BPS = useCallback(
    (showDelta1BPS: boolean) => {
      mixpanel.track(MixpanelEvent.ToggleDelta1BPS, { [MixpanelEventProperty.Enabled]: showDelta1BPS });
      setShowDelta1BPS(showDelta1BPS);
    },
    [mixpanel, setShowDelta1BPS]
  );

  const showCompactToggles = useIsMinWidth(COMPACT_TOGGLE_MIN_WIDTH);
  const showToggleLabels = useIsMinWidth(COMPACT_TOGGLE_LABEL_MIN_WIDTH);
  const togglesMenu = usePopoverState({
    placement: 'bottom-end',
    closeOnClickOutside: true,
    noPaddingAndBorder: true,
    trigger: 'click',
  });
  const showDelta1BPSToggle = hasBPSPricingSecurities && enableDelta1BPSPricingMode;

  return (
    <HeaderWrapper>
      <Routes>
        <Route path="/trading/*" element={<WatchlistButton />} />
      </Routes>
      <HStack justifyContent="flex-start" flex="1 1 0" pl="spacing8">
        <AppBreadcrumbs />
      </HStack>
      <Flex>
        <Routes>
          <Route
            path="/trading/*"
            element={
              showCompactToggles ? (
                <Flex gap="spacing12" pr="spacing12">
                  <Toggle
                    size={FormControlSizes.Small}
                    checked={!!showAllInPrices}
                    onChange={handleToggleAllInPrices}
                    tooltip={FEES_TOGGLE}
                    label="Fees"
                  />
                  <Toggle
                    size={FormControlSizes.Small}
                    checked={showFirmLiquidity}
                    onChange={handleToggleFirmLiquidity}
                    tooltip={FIRM_LIQUIDITY_TOGGLE}
                    label="Firm Liquidity"
                  />
                  {showDelta1BPSToggle && (
                    <>
                      <Tour tour={TourName.v255Delta1BPSToggle} />
                      <Toggle
                        data-testid="delta-1-bps-toggle"
                        size={FormControlSizes.Small}
                        checked={showDelta1BPS}
                        onChange={handleToggleDelta1BPS}
                        tooltip={DELTA_1_BPS_TOGGLE}
                        label="Delta-1 BPS"
                      />
                    </>
                  )}
                </Flex>
              ) : (
                <Popover {...togglesMenu} targetStyle={POPOVER_TARGET_STYLE} zIndex={Z_INDEX.dropdown}>
                  <HeaderButton
                    borderPlacement="left"
                    onClick={() => togglesMenu.open()}
                    size={FormControlSizes.Small}
                    variant={togglesMenu.isOpen ? ButtonVariants.Priority : ButtonVariants.Default}
                    tooltip={
                      !showCompactToggles ? (
                        <Stack gap="spacing8">
                          <Flex gap="spacing2">
                            Fees
                            <Icon
                              data-testid="fees-toggle-indicator"
                              icon={IconName.DotSolid}
                              color={showAllInPrices ? 'colorTextPrimary' : 'colorTextSubtle'}
                            />
                          </Flex>
                          <Flex gap="spacing2">
                            Firm Liquidity
                            <Icon
                              data-testid="firm-liquidity-toggle-indicator"
                              icon={IconName.DotSolid}
                              color={showFirmLiquidity ? 'colorTextPrimary' : 'colorTextSubtle'}
                            />
                          </Flex>
                          {showDelta1BPSToggle && (
                            <Flex gap="spacing2">
                              Delta-1 BPS
                              <Icon
                                data-testid="delta-1-bps-toggle-indicator"
                                icon={IconName.DotSolid}
                                color={showDelta1BPS ? 'colorTextPrimary' : 'colorTextSubtle'}
                              />
                            </Flex>
                          )}
                        </Stack>
                      ) : undefined
                    }
                    data-testid="header-button-display-settings"
                    compact={!showCompactToggles}
                    dim
                  >
                    <HStack gap="spacing4">
                      {showToggleLabels && 'Fees'}
                      <Icon
                        data-testid="fees-toggle-indicator"
                        icon={IconName.DotSolid}
                        color={showAllInPrices ? 'colorTextPrimary' : 'colorTextSubtle'}
                      />
                      {showToggleLabels && 'Firm'}
                      <Icon
                        data-testid="firm-liquidity-toggle-indicator"
                        icon={IconName.DotSolid}
                        color={showFirmLiquidity ? 'colorTextPrimary' : 'colorTextSubtle'}
                      />
                      {showDelta1BPSToggle && (
                        <>
                          {showToggleLabels && 'BPS'}
                          <Icon
                            data-testid="delta-1-bps-toggle-indicator"
                            icon={IconName.DotSolid}
                            color={showDelta1BPS ? 'colorTextPrimary' : 'colorTextSubtle'}
                          />
                        </>
                      )}
                    </HStack>
                  </HeaderButton>
                  <Stack gap="spacing8" p="spacing12" minWidth="140px">
                    <Toggle
                      size={FormControlSizes.Small}
                      checked={!!showAllInPrices}
                      onChange={handleToggleAllInPrices}
                      tooltip={FEES_TOGGLE}
                      tooltipProps={COMPACT_TOGGLE_TOOLTIP_PROPS}
                      label="Fees"
                      justifyContent="space-between"
                    />
                    <Toggle
                      size={FormControlSizes.Small}
                      checked={showFirmLiquidity}
                      onChange={handleToggleFirmLiquidity}
                      tooltip={FIRM_LIQUIDITY_TOGGLE}
                      tooltipProps={COMPACT_TOGGLE_TOOLTIP_PROPS}
                      label="Firm Liquidity"
                      justifyContent="space-between"
                    />
                    {showDelta1BPSToggle && (
                      <Toggle
                        data-testid="delta-1-bps-toggle"
                        size={FormControlSizes.Small}
                        checked={showDelta1BPS}
                        onChange={handleToggleDelta1BPS}
                        tooltip={DELTA_1_BPS_TOGGLE}
                        tooltipProps={COMPACT_TOGGLE_TOOLTIP_PROPS}
                        label="Delta-1 BPS"
                        justifyContent="space-between"
                      />
                    )}
                  </Stack>
                </Popover>
              )
            }
          />
        </Routes>
        <HeaderButtons />
      </Flex>
    </HeaderWrapper>
  );
};
